/**
 * ReportBuilderPage — `/reporting/reports/builder`.
 *
 * Two-panel interface:
 *   1. Pick a registered report key + date range → run preview via
 *      POST /v1/reporting/preview (real endpoint, B-slice 5).
 *   2. Draft composer (display name, description, fields) — local-only until
 *      the Phase 7+ POST /v1/reporting/definitions endpoint ships.
 */
import { type JSX, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Database, Filter, Plus, Save, Search, Trash2 } from 'lucide-react';

import { previewReport, type ReportRunWire } from '../../lib/api/reporting';
import { PageHeader } from '../shared/StaffUI';

interface DraftField {
  readonly id: string;
  readonly source: string;
  readonly fieldName: string;
  readonly aggregate: 'NONE' | 'SUM' | 'AVG' | 'COUNT' | 'MAX' | 'MIN';
}

interface DraftDefinition {
  readonly displayName: string;
  readonly description: string;
  readonly category: string;
  readonly fields: readonly DraftField[];
}

const STORAGE_KEY = 'upmp.reporting.report-builder.draft';

function loadDraft(): DraftDefinition {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw === null) {
      return { displayName: '', description: '', category: 'Operations', fields: [] };
    }
    return JSON.parse(raw) as DraftDefinition;
  } catch {
    return { displayName: '', description: '', category: 'Operations', fields: [] };
  }
}

function saveDraft(d: DraftDefinition): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(d));
}

const REPORT_KEYS = [
  'DAILY_REVENUE_BY_REGION',
  'MAIL_VOLUME_TRENDS',
  'CUSTOMER_ACQUISITION_FUNNEL',
  'DRIVER_PERFORMANCE',
  'WALLET_ACTIVITY_SUMMARY',
  'AUDIT_COMPLIANCE_HEATMAP',
  'NIPOST_PAY_ADOPTION',
  'GOVERNMENT_SERVICE_THROUGHPUT',
  'RECEIPT_INVOICE_KPIS',
] as const;

const SOURCES: readonly string[] = [
  'mail.MailItem',
  'payment.Payment',
  'finance.Settlement',
  'delivery.Delivery',
  'kyc.Verification',
  'government.MdaApplication',
  'franchise.Transaction',
];

export function ReportBuilderPage(): JSX.Element {
  const [draft, setDraft] = useState<DraftDefinition>(() => loadDraft());
  const [source, setSource] = useState<string>(SOURCES[0] ?? '');
  const [fieldName, setFieldName] = useState('');
  const [aggregate, setAggregate] = useState<DraftField['aggregate']>('SUM');

  // Preview runner — uses registered report key
  const [previewKey, setPreviewKey] = useState<string>(REPORT_KEYS[0]);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  const preview = useMutation<ReportRunWire, Error>({
    mutationFn: () =>
      previewReport(previewKey, { fromDate, toDate }),
  });

  function update(next: DraftDefinition): void {
    setDraft(next);
    saveDraft(next);
  }

  function addField(): void {
    if (fieldName.trim().length === 0) return;
    const next: DraftDefinition = {
      ...draft,
      fields: [
        ...draft.fields,
        { id: `f-${Date.now()}`, source, fieldName: fieldName.trim(), aggregate },
      ],
    };
    update(next);
    setFieldName('');
  }

  function removeField(id: string): void {
    update({ ...draft, fields: draft.fields.filter((f) => f.id !== id) });
  }

  function submitPreview(e: React.FormEvent): void {
    e.preventDefault();
    if (!fromDate || !toDate) return;
    preview.mutate();
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Custom report builder"
        description="Run a registered report preview or compose a draft definition."
      />

      {/* Panel 1 — Run a registered report */}
      <div className="bg-white border border-border rounded-xl p-5">
        <div className="flex items-center gap-2 mb-4">
          <Search className="w-4 h-4 text-nipost-green" />
          <h3 className="font-semibold text-gray-900">Run registered report (preview)</h3>
        </div>
        <form onSubmit={submitPreview} className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <select
            value={previewKey}
            onChange={(e) => setPreviewKey(e.target.value)}
            aria-label="Report key"
            data-testid="report-key-select"
            className="px-3 py-2 text-sm border border-border rounded-lg md:col-span-2"
          >
            {REPORT_KEYS.map((k) => (
              <option key={k} value={k}>{k}</option>
            ))}
          </select>
          <input
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            aria-label="From date"
            data-testid="report-from-date"
            className="px-3 py-2 text-sm border border-border rounded-lg"
          />
          <input
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            aria-label="To date"
            data-testid="report-to-date"
            className="px-3 py-2 text-sm border border-border rounded-lg"
          />
          <div className="md:col-span-4">
            <button
              type="submit"
              disabled={preview.isPending || !fromDate || !toDate}
              data-testid="report-run-btn"
              className="inline-flex items-center gap-2 px-4 py-2 text-xs font-medium text-white bg-nipost-green rounded-lg hover:bg-nipost-green/90 transition-colors disabled:opacity-50"
            >
              <Search className="w-3.5 h-3.5" />
              {preview.isPending ? 'Running…' : 'Run preview'}
            </button>
          </div>
        </form>

        {preview.isError && (
          <p className="mt-3 text-xs text-red-600">{preview.error.message}</p>
        )}
        {preview.data && (
          <div className="mt-4">
            <p className="text-xs font-medium text-gray-700 mb-2">
              Preview result — <span className="text-nipost-green">{preview.data.status}</span>{' '}
              in {preview.data.durationMs ?? '—'} ms
            </p>
            <pre className="p-3 bg-gray-50 border border-border rounded-lg text-[11px] font-mono overflow-x-auto max-h-64">
              {JSON.stringify(preview.data.result, null, 2)}
            </pre>
          </div>
        )}
      </div>

      {/* Panel 2 — Draft definition (local) */}
      <div className="bg-white border border-border rounded-xl p-5">
        <h3 className="font-semibold text-gray-900 mb-1">Draft report definition</h3>
        <p className="text-xs text-muted mb-4">
          Saved locally for now. Publishing report definitions to the shared catalogue
          ships in a later release.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <input
            type="text"
            value={draft.displayName}
            onChange={(e) => update({ ...draft, displayName: e.target.value })}
            placeholder="Display name"
            aria-label="Display name"
            data-testid="report-display-name"
            className="px-3 py-2 text-sm border border-border rounded-lg md:col-span-2"
          />
          <select
            value={draft.category}
            onChange={(e) => update({ ...draft, category: e.target.value })}
            aria-label="Category"
            className="px-3 py-2 text-sm border border-border rounded-lg"
          >
            <option>Operations</option>
            <option>Finance</option>
            <option>Compliance</option>
            <option>Franchise</option>
            <option>Delivery</option>
            <option>Government</option>
          </select>
          <textarea
            value={draft.description}
            onChange={(e) => update({ ...draft, description: e.target.value })}
            placeholder="Description"
            aria-label="Description"
            rows={2}
            className="px-3 py-2 text-sm border border-border rounded-lg md:col-span-3"
          />
        </div>
      </div>

      <div className="bg-white border border-border rounded-xl p-5">
        <div className="flex items-center gap-2 mb-3">
          <Database className="w-4 h-4 text-nipost-green" />
          <h3 className="font-semibold text-gray-900">Add field</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <select
            value={source}
            onChange={(e) => setSource(e.target.value)}
            aria-label="Source"
            className="px-3 py-2 text-sm border border-border rounded-lg"
          >
            {SOURCES.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
          <input
            type="text"
            value={fieldName}
            onChange={(e) => setFieldName(e.target.value)}
            placeholder="Field (eg. amountMinor)"
            aria-label="Field name"
            className="px-3 py-2 text-sm border border-border rounded-lg md:col-span-2"
          />
          <select
            value={aggregate}
            onChange={(e) => setAggregate(e.target.value as DraftField['aggregate'])}
            aria-label="Aggregate"
            className="px-3 py-2 text-sm border border-border rounded-lg"
          >
            <option>NONE</option>
            <option>SUM</option>
            <option>AVG</option>
            <option>COUNT</option>
            <option>MAX</option>
            <option>MIN</option>
          </select>
        </div>
        <button
          type="button"
          onClick={addField}
          className="mt-3 inline-flex items-center gap-1 px-3 py-2 text-xs font-medium text-white bg-nipost-green rounded-lg hover:bg-nipost-green/90 transition-colors"
        >
          <Plus className="w-3.5 h-3.5" /> Add field
        </button>
      </div>

      <div className="bg-white border border-border rounded-xl p-5">
        <div className="flex items-center gap-2 mb-3">
          <Filter className="w-4 h-4 text-nipost-green" />
          <h3 className="font-semibold text-gray-900">Fields ({draft.fields.length})</h3>
        </div>
        {draft.fields.length === 0 ? (
          <p className="text-sm text-muted">No fields yet.</p>
        ) : (
          <ul className="divide-y divide-border">
            {draft.fields.map((f) => (
              <li key={f.id} className="flex items-center justify-between py-3" data-testid="draft-field-row">
                <div>
                  <p className="text-sm font-medium text-gray-900">{f.fieldName}</p>
                  <p className="text-xs text-muted">
                    <code className="font-mono">{f.source}</code> · {f.aggregate}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => removeField(f.id)}
                  aria-label={`Remove ${f.fieldName}`}
                  className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-red-600 hover:bg-red-50 rounded"
                >
                  <Trash2 className="w-3.5 h-3.5" /> Remove
                </button>
              </li>
            ))}
          </ul>
        )}

        <div className="mt-4 flex items-center gap-2">
          <Save className="w-3.5 h-3.5 text-muted" />
          <span className="text-xs text-muted">Auto-saved on every change.</span>
        </div>
      </div>
    </div>
  );
}
